import { LineItemType, MerchandiseCatalogItem, MerchandiseCatalogItemStatus, MerchandisePurchaseItem } from "types";
import { FulfillmentUnitOperationType } from "./updateQuantityPayloadHelper";
import {
    getMerchandiseVariantPrice,
    getSortedPurchasableMerchandiseCatalogItems,
    hasCatalogItemPrice,
} from "@/models/Merchandise";
import { getFulfilledQuantity } from "@/models/MerchandisePurchaseItem";

export type ReturnOperationErrorsByTemplateId = Record<string, string[]>;
export type QuantityOperationErrorsByTemplateId = Record<string, string[]>;
export type QuantityOverridesByTemplateId = Record<string, number>;

export interface VariantQuantity {
    catalogItem?: MerchandiseCatalogItem;
    templateId: ID;
    unitId: ID;
    lineItemId?: ID;
    variantName: string;
    price: number;
    currentQuantity: number;
    newQuantity: number;
    fulfilledQuantity: number;
    available: boolean;
    hasPrice: boolean;
    isHidden: boolean;
}

interface ReturnOperationNode {
    id?: unknown;
    status?: unknown;
    reason?: unknown;
    message?: unknown;
    error?: {
        reason?: unknown;
        message?: unknown;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

interface ReturnOperationError {
    response?: {
        data?: {
            field?: {
                ops?: ReturnOperationNode[];
            };
        };
    };
}

type OperationPanel = "quantity" | "return";

interface ReturnOperationParseResult {
    quantityErrorByTemplateId: QuantityOperationErrorsByTemplateId;
    returnErrorByTemplateId: ReturnOperationErrorsByTemplateId;
    quantitySuccessTemplateIds: string[];
    returnSuccessTemplateIds: string[];
}

const RETURN_OPERATION_ID_PREFIX = `${FulfillmentUnitOperationType.RETURN}-`;
const REMOVE_OPERATION_ID_PREFIX = `${FulfillmentUnitOperationType.REMOVE}-`;

const pushUniqueErrorMessage = (errorByTemplateId: Record<string, string[]>, templateId: string, message: string) => {
    if (!errorByTemplateId[templateId]) {
        errorByTemplateId[templateId] = [message];
        return;
    }

    if (!errorByTemplateId[templateId].includes(message)) {
        errorByTemplateId[templateId].push(message);
    }
};

const getOperationContext = (entryId: string): { templateId?: string; panel: OperationPanel } => {
    const isReturnOperationId =
        entryId.startsWith(RETURN_OPERATION_ID_PREFIX) || entryId.startsWith(REMOVE_OPERATION_ID_PREFIX);

    if (!isReturnOperationId) {
        return { templateId: entryId, panel: "quantity" };
    }

    const idWithoutPrefix = entryId.startsWith(RETURN_OPERATION_ID_PREFIX)
        ? entryId.slice(RETURN_OPERATION_ID_PREFIX.length)
        : entryId.slice(REMOVE_OPERATION_ID_PREFIX.length);

    const hasIndexSuffix = /-\d+$/.test(idWithoutPrefix);
    const templateId = hasIndexSuffix ? idWithoutPrefix.replace(/-\d+$/, "") : idWithoutPrefix;

    return {
        templateId: templateId || undefined,
        panel: "return",
    };
};

export const getReturnOperationErrorsByTemplateId = (error: ReturnOperationError): ReturnOperationParseResult => {
    const operationEntries = error?.response?.data?.field?.ops ?? [];

    const quantityErrorByTemplateId: QuantityOperationErrorsByTemplateId = {};
    const returnErrorByTemplateId: ReturnOperationErrorsByTemplateId = {};
    const quantitySuccessTemplateIdSet = new Set<string>();
    const returnSuccessTemplateIdSet = new Set<string>();

    for (const operationEntry of operationEntries) {
        const entryId = typeof operationEntry.id === "string" ? operationEntry.id : undefined;
        if (!entryId) {
            continue;
        }

        const { templateId, panel } = getOperationContext(entryId);

        if (!templateId) {
            continue;
        }

        const status = typeof operationEntry.status === "string" ? operationEntry.status : undefined;
        const reason = operationEntry.error?.reason ?? operationEntry.reason;
        const message = operationEntry.error?.message ?? operationEntry.message;

        if (status === "success") {
            if (panel === "quantity") {
                quantitySuccessTemplateIdSet.add(templateId);
            } else {
                returnSuccessTemplateIdSet.add(templateId);
            }
            continue;
        }

        if (status === "error" || status === undefined) {
            const nextMessage = message ?? reason;

            if (typeof nextMessage !== "string") {
                continue;
            }

            if (panel === "quantity") {
                pushUniqueErrorMessage(quantityErrorByTemplateId, templateId, nextMessage);
                continue;
            }

            pushUniqueErrorMessage(returnErrorByTemplateId, templateId, nextMessage);
        }
    }

    return {
        quantityErrorByTemplateId,
        returnErrorByTemplateId,
        quantitySuccessTemplateIds: [...quantitySuccessTemplateIdSet],
        returnSuccessTemplateIds: [...returnSuccessTemplateIdSet],
    };
};

export const getVariantQuantitiesWithOverrides = (
    baseVariantQuantities: VariantQuantity[],
    quantityOverridesByTemplateId: QuantityOverridesByTemplateId,
): VariantQuantity[] => {
    return baseVariantQuantities.map((variant) => ({
        ...variant,
        newQuantity: quantityOverridesByTemplateId[variant.templateId] ?? variant.newQuantity,
    }));
};

export const getChangedVariants = (variantQuantities: VariantQuantity[]): VariantQuantity[] => {
    return variantQuantities.filter((variant) => variant.newQuantity !== variant.currentQuantity);
};

export const hasOnlyNewVariantAdditions = (changedVariants: VariantQuantity[]): boolean => {
    if (changedVariants.length === 0) {
        return false;
    }

    return changedVariants.every((variant) => variant.currentQuantity === 0 && variant.newQuantity > 0);
};

export const getInitializeVariantQuantities = (
    catalogItems: MerchandiseCatalogItem[],
    item: MerchandisePurchaseItem,
): VariantQuantity[] => {
    const sortedCatalogItems = getSortedPurchasableMerchandiseCatalogItems(catalogItems);
    const wareLineItems = item.lineItems?.filter((lineItem) => lineItem.type === LineItemType.WARE) ?? [];
    const catalogTemplateIds = new Set(
        catalogItems.map((catalogItem) => catalogItem.template?.id).filter(Boolean) as ID[],
    );
    const hiddenTemplateIds = new Set(
        catalogItems
            .filter((catalogItem) => catalogItem.status !== MerchandiseCatalogItemStatus.PUBLISHED)
            .map((catalogItem) => catalogItem.template?.id)
            .filter(Boolean) as ID[],
    );

    if (sortedCatalogItems.length === 0 && wareLineItems.length === 0) {
        return [];
    }

    const variants: VariantQuantity[] = [];
    const includedTemplateIds = new Set<ID>();

    for (const catalogItem of sortedCatalogItems) {
        if (!catalogItem.template?.id) {
            continue;
        }

        const lineItem = wareLineItems.find((wareLineItem) => wareLineItem.template?.id === catalogItem.template?.id);

        variants.push({
            catalogItem,
            templateId: catalogItem.template.id,
            unitId: catalogItem.unit.id,
            lineItemId: lineItem?.id,
            variantName: catalogItem.name,
            price: getMerchandiseVariantPrice(catalogItem),
            currentQuantity: lineItem?.quantity ?? 0,
            newQuantity: lineItem?.quantity ?? 0,
            fulfilledQuantity: getFulfilledQuantity(item, catalogItem.template.id),
            available: catalogItem.status === MerchandiseCatalogItemStatus.PUBLISHED,
            hasPrice: hasCatalogItemPrice(catalogItem),
            isHidden: false,
        });

        includedTemplateIds.add(catalogItem.template.id);
    }

    for (const lineItem of wareLineItems) {
        const templateId = lineItem.template?.id;
        if (!templateId || includedTemplateIds.has(templateId)) {
            continue;
        }

        const isHidden = hiddenTemplateIds.has(templateId) || !catalogTemplateIds.has(templateId);
        const quantity = lineItem.quantity ?? 0;
        const unitIdFromFulfillment = item.fulfillmentUnits?.find(
            (fulfillmentUnit) => fulfillmentUnit.template.id === templateId,
        )?.unit.id;

        variants.push({
            templateId,
            lineItemId: lineItem.id,
            variantName: lineItem.name ?? "Variant",
            price: lineItem.amount && quantity > 0 ? lineItem.amount / quantity : 0,
            currentQuantity: quantity,
            newQuantity: quantity,
            fulfilledQuantity: getFulfilledQuantity(item, templateId),
            available: !isHidden,
            hasPrice: true,
            isHidden,
            unitId: unitIdFromFulfillment ?? "",
        });
    }

    return variants;
};

export const getTotalQuantity = (variants: VariantQuantity[]): number => {
    return variants.reduce((sum, value) => sum + value.newQuantity, 0);
};

/**
 * Validate that at least one variant has quantity > 0
 */
export const validateMinimumQuantity = (variants: VariantQuantity[]): boolean => {
    return getTotalQuantity(variants) > 0;
};
